import { Layer, UNIT, picking, project32 } from "@deck.gl/core";
import type {
  Accessor,
  AccessorFunction,
  DefaultProps,
  GetPickingInfoParams,
  LayerDataSource,
  LayerProps,
  PickingInfo,
  Unit,
  UpdateParameters,
} from "@deck.gl/core";
import { Geometry } from "@luma.gl/engine";
import { Model } from "@luma.gl/engine";
import { ShaderModule } from "@luma.gl/shadertools";
import { Position } from "geojson";

import { getDistanceInM } from "@nens/shared-utils";

import type { PathGeometry } from "./waterflow-utils/path";
import PathTesselator from "./waterflow-utils/path-tesselator";
import fs from "./waterflow-utils/waterflow-layer.frag";
import vs from "./waterflow-utils/waterflow-layer.vert";

export type WaterFlowPath = {
  coordinates: Position[];
  id: number;
  width: number;
  speed: number;
  passedTime: number;
  averageSpeedKey: number;
  modelType: string;
  maxWidth?: number;
  pipeType?: number;
};

const uniformBlock = `\
layout(std140) uniform flowUniforms {
  float flowTreshold;
  float flowHighlightTreshold;
  float miterLimit;
  float widthScale;
  float widthMinPixels;
  float widthMaxPixels;
  float jointType;
  float capType;
  bool billboard;
  float widthUnits;
  vec3 color;
  float latestUpdateTime;
  float simulationRunningType;
  float currentTime;
} flow;
`;

type FlowUniformProps = {
  flowTreshold: number;
  flowHighlightTreshold: number;
  miterLimit: number;
  widthScale: number;
  widthMinPixels: number;
  widthMaxPixels: number;
  jointType: number;
  capType: number;
  billboard: boolean;
  widthUnits: number;
  color: [number, number, number];

  latestUpdateTime: number;
  simulationRunningType: number;
  currentTime: number;
};

export type FlowProps = FlowUniformProps;

export const flowUniforms = {
  name: "flow",
  vs: uniformBlock,
  fs: uniformBlock,
  uniformTypes: {
    flowTreshold: "f32",
    flowHighlightTreshold: "f32",
    miterLimit: "f32",
    widthScale: "f32",
    widthMinPixels: "f32",
    widthMaxPixels: "f32",
    jointType: "f32",
    capType: "f32",
    billboard: "f32",
    widthUnits: "f32",
    color: "vec3<f32>",

    latestUpdateTime: "f32",
    simulationRunningType: "f32",
    currentTime: "f32",
  },
} as const satisfies ShaderModule<FlowProps>;

type WaterflowLayerOwnProps<DataT> = {
  data: LayerDataSource<DataT>;
  /** The units of the line width, one of `'meters'`, `'common'`, and `'pixels'`
   * @default 'meters'
   */
  widthUnits?: Unit;
  /**
   * Path width multiplier.
   * @default 1
   */
  widthScale?: number;
  /**
   * The minimum path width in pixels. This prop can be used to prevent the path from getting too thin when zoomed out.
   * @default 0
   */
  widthMinPixels?: number;
  /**
   * The maximum path width in pixels. This prop can be used to prevent the path from getting too thick when zoomed in.
   * @default Number.MAX_SAFE_INTEGER
   */
  widthMaxPixels?: number;
  /**
   * Type of joint. If `true`, draw round joints. Otherwise draw miter joints.
   * @default false
   */
  jointRounded?: boolean;
  /**
   * Type of caps. If `true`, draw round caps. Otherwise draw square caps.
   * @default false
   */
  capRounded?: boolean;
  /**
   * The maximum extent of a joint in ratio to the stroke width. Only works if `jointRounded` is `false`.
   * @default 4
   */
  miterLimit?: number;
  /**
   * If `true`, extrude the path in screen space (width always faces the camera).
   * If `false`, the width always faces up (z).
   * @default false
   */
  billboard?: boolean;
  /**
   * (Experimental) If `'loop'` or `'open'`, will skip normalizing the coordinates returned by `getPath` and instead assume all paths are to be loops or open paths.
   * When normalization is disabled, paths must be specified in the format of flat array. Open paths must contain at least 2 vertices and closed paths must contain at least 3 vertices.
   * @default null
   */
  _pathType?: null | "loop" | "open";
  /**
   * Path geometry accessor.
   */
  getPath?: AccessorFunction<DataT, PathGeometry>;
  /**
   * Path width accessor.
   * @default 1
   */
  getWidth?: Accessor<DataT, number | number[]>;
  /**
   * @deprecated Use `jointRounded` and `capRounded` instead
   */
  rounded?: boolean;
  /**
   * Minimum speed flow has to go
   * @default [255, 255, 255]
   */
  color?: [number, number, number];
  /**
   * Minimum speed flow has to go
   * @default 0
   */
  flowTreshold?: number;
  /**
   * Minimum speed flow has to have before being highlighted
   * @default 0
   */
  flowHighlightTreshold?: number;
  /**
   * The time frame of the last update.
   * @default 0
   */
  latestUpdateTime?: number;
  /**
   * Simulation is running or not.
   * @default false
   */
  simulationRunning?: boolean;
  /**
   * The current time of the frame.
   * @default 0
   */
  currentTime?: number;
  /**
   * Trail length accessor.
   */
  getTrailLength?: AccessorFunction<DataT, number | number[]>;
  /**
   *  Passed time accessor.
   */
  getPassedTime?: AccessorFunction<DataT, number | number[]>;
  /**
   * Speed accessor.
   */
  getSpeed?: AccessorFunction<DataT, number | number[]>;
  /**
   * Timestamp accessor.
   */
  getTimestamps?: AccessorFunction<DataT, number[]>;
};

export type WaterflowLayerProps<DataT = WaterFlowPath[]> = WaterflowLayerOwnProps<DataT> &
  LayerProps;

const defaultProps: DefaultProps<WaterflowLayerProps> = {
  widthUnits: "meters",
  widthScale: { type: "number", min: 0, value: 1 },
  widthMinPixels: { type: "number", min: 0, value: 0 },
  widthMaxPixels: { type: "number", min: 0, value: Number.MAX_SAFE_INTEGER },
  jointRounded: false,
  capRounded: false,
  miterLimit: { type: "number", min: 0, value: 4 },
  billboard: false,

  // deprecated props
  rounded: { deprecatedFor: ["jointRounded", "capRounded"] },

  latestUpdateTime: { type: "number", value: 0 },
  simulationRunning: false,
  color: { type: "color", value: [255, 255, 255] },
  flowTreshold: { type: "number", value: 10 },
  flowHighlightTreshold: { type: "number", value: 10 },
  currentTime: { type: "number", value: 0 },
  getPath: (d) => d.flatMap((p) => p.coordinates).flat(),
  positionFormat: `XY`,
  _pathType: "open",
  getPassedTime: (d) => d.flatMap((p) => p.coordinates.flatMap(() => p.passedTime)),
  getSpeed: (d) => d.flatMap((p) => p.coordinates.flatMap(() => p.speed)),
  getWidth: (d) => d.flatMap((p) => p.coordinates.flatMap(() => (p.width > 1 ? p.width : 1))),
  getTrailLength: (d) => {
    const speedArray = d.flatMap((p) => {
      return p.coordinates.flatMap(() => p.speed);
    });
    const average = Math.abs(speedArray.reduce((a, b) => a + b, 0) / speedArray.length);
    return d.flatMap((p) =>
      p.coordinates.flatMap((_) => {
        return average ? 60 / average : 0;
      })
    );
  },
  getTimestamps: (d) => {
    let distance = 0;
    return d.flatMap((p: WaterFlowPath, pathIndex) =>
      p.coordinates.flatMap((coordinate, coordIndex) => {
        const lastCoords = p.coordinates[coordIndex - 1];
        const lastPath = d[pathIndex - 1];
        // Get distance from last coordinate
        if (lastCoords) {
          const ownSpeed = Math.abs(p.speed) > 0.1 ? Math.abs(p.speed) : 1;
          distance += getDistanceInM(coordinate, lastCoords) / ownSpeed;
        }
        // Get distance from last path if current coordinate is the first of the path
        else if (lastPath) {
          const lastPathSpeed = Math.abs(lastPath.speed);
          const lastPathCoords = lastPath.coordinates;
          distance +=
            getDistanceInM(coordinate, lastPathCoords[lastPathCoords.length - 1]) / lastPathSpeed;
        }
        return distance;
      })
    );
  },
};

const ATTRIBUTE_TRANSITION = {
  enter: (value: any, chunk: any) => {
    return chunk.length ? chunk.subarray(chunk.length - value.length) : value;
  },
};

/** Render lists of coordinate points as extruded polylines with mitering. */
export class WaterflowLayer<DataT = any, ExtraPropsT extends object = object> extends Layer<
  ExtraPropsT & Required<WaterflowLayerOwnProps<DataT>>
> {
  static override defaultProps = defaultProps;
  static override layerName = "WaterflowLayer";

  override state!: {
    model?: Model;
    pathTesselator: PathTesselator;
  };

  override getShaders() {
    return super.getShaders({ vs, fs, modules: [project32, picking, flowUniforms] }); // 'project' module added by default.
  }

  override get wrapLongitude(): boolean {
    return false;
  }

  override getBounds(): [number[], number[]] | null {
    return this.getAttributeManager()?.getBounds(["vertexPositions"]);
  }

  initializeState() {
    const noAlloc = true;
    const attributeManager = this.getAttributeManager();

    attributeManager!.addInstanced({
      vertexPositions: {
        size: 3,
        // Start filling buffer from 1 vertex in
        vertexOffset: 1,
        type: "float64",
        fp64: this.use64bitPositions(),
        transition: ATTRIBUTE_TRANSITION,
        accessor: "getPath",

        update: this.calculatePositions,
        noAlloc,
        shaderAttributes: {
          instanceLeftPositions: {
            vertexOffset: 0,
          },
          instanceStartPositions: {
            vertexOffset: 1,
          },
          instanceEndPositions: {
            vertexOffset: 2,
          },
          instanceRightPositions: {
            vertexOffset: 3,
          },
        },
      },
      instanceTypes: {
        size: 1,
        type: "uint8",

        update: this.calculateSegmentTypes,
        noAlloc,
      },
      instanceStrokeWidths: {
        size: 1,
        accessor: "getWidth",
        transition: ATTRIBUTE_TRANSITION,
        defaultValue: 1,
      },
      timestamps: {
        size: 1,
        accessor: "getTimestamps",
        shaderAttributes: {
          instanceTimestamps: {
            size: 1,
            vertexOffset: 0,
          },
          instanceNextTimestamps: {
            size: 1,

            vertexOffset: 1,
          },
        },
      },
      instanceTrailLength: {
        size: 1,
        fp64: true,
        accessor: "getTrailLength",
      },
      instanceSpeed: {
        size: 1,
        accessor: "getSpeed",
      },
      instancePassedTime: {
        size: 1,
        accessor: "getPassedTime",
      },
    });

    this.setState({
      pathTesselator: new PathTesselator({
        fp64: this.use64bitPositions(),
      }),
    });
  }

  override updateState(params: UpdateParameters<this>) {
    super.updateState(params);
    const { props, changeFlags } = params;

    const attributeManager = this.getAttributeManager();

    const geometryChanged =
      changeFlags.dataChanged ||
      (changeFlags.updateTriggersChanged &&
        (changeFlags.updateTriggersChanged.all || changeFlags.updateTriggersChanged.getPath));

    if (geometryChanged) {
      const { pathTesselator } = this.state;
      const buffers = (props.data as any).attributes || {};

      pathTesselator.updateGeometry({
        data: props.data,
        geometryBuffer: buffers.getPath,
        buffers,
        normalize: !props._pathType,
        loop: props._pathType === "loop",
        getGeometry: props.getPath,
        positionFormat: props.positionFormat,
        wrapLongitude: props.wrapLongitude,
        // TODO - move the flag out of the viewport
        resolution: this.context.viewport.resolution,
        dataChanged: changeFlags.dataChanged,
      });
      this.setState({
        numInstances: pathTesselator.instanceCount,
        startIndices: pathTesselator.vertexStarts,
      });
      if (!changeFlags.dataChanged) {
        // Base `layer.updateState` only invalidates all attributes on data change
        // Cover the rest of the scenarios here
        attributeManager!.invalidateAll();
      }
    }

    if (changeFlags.extensionsChanged) {
      this.state.model?.destroy();
      this.state.model = this._getModel();
      attributeManager!.invalidateAll();
    }
  }

  override getPickingInfo(params: GetPickingInfoParams): PickingInfo {
    const info = super.getPickingInfo(params);
    const { index } = info;
    const data = this.props.data as any[];

    // Check if data comes from a composite layer, wrapped with getSubLayerRow
    if (data[0] && data[0].__source) {
      // index decoded from picking color refers to the source index
      info.object = data.find((d) => d.__source.index === index);
    }
    return info;
  }

  /** Override base Layer method */
  override disablePickingIndex(objectIndex: number) {
    const data = this.props.data as any[];

    // Check if data comes from a composite layer, wrapped with getSubLayerRow
    if (data[0] && data[0].__source) {
      // index decoded from picking color refers to the source index
      for (let i = 0; i < data.length; i++) {
        if (data[i].__source.index === objectIndex) {
          this._disablePickingIndex(i);
        }
      }
    } else {
      super.disablePickingIndex(objectIndex);
    }
  }

  override draw() {
    const {
      jointRounded,
      capRounded,
      billboard,
      miterLimit,
      widthUnits,
      widthScale,
      widthMinPixels,
      widthMaxPixels,
      simulationRunning,
      color,
      flowTreshold,
      flowHighlightTreshold,
      latestUpdateTime,
      currentTime,
    } = this.props;

    const model = this.state.model!;
    const flowProps: FlowProps = {
      color,
      flowTreshold,
      flowHighlightTreshold,
      simulationRunningType: Number(simulationRunning),
      latestUpdateTime,
      currentTime,
      jointType: Number(jointRounded),
      capType: Number(capRounded),
      billboard,
      widthUnits: UNIT[widthUnits],
      widthScale,
      miterLimit,
      widthMinPixels,
      widthMaxPixels,
    };

    model.shaderInputs.setProps({
      flow: flowProps,
    });
    model.draw(this.context.renderPass);
  }

  protected _getModel(): Model {
    /*
     *       _
     *        "-_ 1                   3                       5
     *     _     "o---------------------o-------------------_-o
     *       -   / ""--..__              '.             _.-' /
     *   _     "@- - - - - ""--..__- - - - x - - - -_.@'    /
     *    "-_  /                   ""--..__ '.  _,-` :     /
     *       "o----------------------------""-o'    :     /
     *      0,2                            4 / '.  :     /
     *                                      /   '.:     /
     *                                     /     :'.   /
     *                                    /     :  ', /
     *                                   /     :     o
     */

    // prettier-ignore
    const SEGMENT_INDICES = [
      // start corner
      0, 1, 2,
      // body
      1, 4, 2,
      1, 3, 4,
      // end corner
      3, 5, 4
    ];

    // [0] position on segment - 0: start, 1: end
    // [1] side of path - -1: left, 0: center (joint), 1: right
    // prettier-ignore
    const SEGMENT_POSITIONS = [
      // bevel start corner
      0, 0,
      // start inner corner
      0, -1,
      // start outer corner
      0, 1,
      // end inner corner
      1, -1,
      // end outer corner
      1, 1,
      // bevel end corner
      1, 0
    ];

    return new Model(this.context.device, {
      ...this.getShaders(),
      id: this.props.id,
      bufferLayout: this.getAttributeManager()!.getBufferLayouts(),
      geometry: new Geometry({
        topology: "triangle-list",
        attributes: {
          indices: new Uint16Array(SEGMENT_INDICES),
          positions: { value: new Float32Array(SEGMENT_POSITIONS), size: 2 },
        },
      }),
      isInstanced: true,
    });
  }

  protected calculatePositions(attribute: any) {
    const { pathTesselator } = this.state;

    attribute.startIndices = pathTesselator.vertexStarts;
    attribute.value = pathTesselator.get("positions");
  }

  protected calculateSegmentTypes(attribute: any) {
    const { pathTesselator } = this.state;

    attribute.startIndices = pathTesselator.vertexStarts;
    attribute.value = pathTesselator.get("segmentTypes");
  }
}
